import Box from "@mui/material/Box";
import { styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import Link from "next/link";
import { getUrls } from "@portal/app/_common/lib/urls";
import { VocationIndustryToName } from "@portal/app/_common/types/vocationIndustry";
import { kebabCase } from "@portal/app/_common/lib/changeCase";
import { genFilterAnalyticsMixpanelArgs } from "../../utils/searchHelpers";
import { useMixpanel } from "@portal/app/_components/hooks/useMixpanel";
import { useDreamSearchContext } from "../../DreamSearchContext";
import { useDreamSearchFilterContext } from "./DreamSearchFilterContext";
import { filterObjectByKeys } from "@portal/app/health/_components/components/filter/DreamSearchFilter/DreamSearchFilter";
import { vocationSubindustryFriendlyMap } from "@portal/app/_common/types/vocationSubindustry";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose } from "@fortawesome/pro-solid-svg-icons/faClose";

export const DreamSearchSubindustryFilter = () => {
  const analytics = useMixpanel();
  const { filters, setFilters } = useDreamSearchFilterContext();
  const { totalCount, filterSettings } = useDreamSearchContext();
  const subindustryOptions = filterSettings?.subindustries;
  const subindustryToFriendlyMap = filterObjectByKeys<GQL.VocationSubindustry>(
    vocationSubindustryFriendlyMap,
    subindustryOptions
  );

  const shouldShowSubindustries = totalCount && subindustryOptions?.length;
  const hasSelectedSubindustry = !!filters.program_vocation_subindustries_in?.length;
  return shouldShowSubindustries ? (
    <PresetFilter className="subindustry_filter" data-testid="subindustry-filter">
      <PresetFilterTitle>What programs are you looking for?</PresetFilterTitle>
      <PresetFilterItems>
        {Object.entries(subindustryToFriendlyMap)
          .filter(([key]) =>
            hasSelectedSubindustry
              ? !!filters.program_vocation_subindustries_in?.includes(
                  key as GQL.VocationSubindustry
                )
              : true
          )
          .map(([key, friendlyValue]) => (
            <PresetFilterItem
              key={key}
              onClick={() => {
                if (
                  filters.program_vocation_subindustries_in?.includes(
                    key as GQL.VocationSubindustry
                  )
                ) {
                  setFilters((prevFilters) => ({
                    ...prevFilters,
                    program_vocation_subindustries_in:
                      prevFilters.program_vocation_subindustries_in?.filter(
                        (subindustry) => subindustry !== key
                      ),
                  }));
                } else {
                  setFilters((prevFilters) => ({
                    ...prevFilters,
                    program_vocation_subindustries_in: [
                      ...(prevFilters.program_vocation_subindustries_in || []),
                      key as GQL.VocationSubindustry,
                    ],
                  }));
                }
                const { event_name, properties } = genFilterAnalyticsMixpanelArgs(
                  "program_vocation_subindustries_in",
                  friendlyValue,
                  filters
                );
                return analytics.track(event_name, properties);
              }}
              data-testid={kebabCase(`dream-search-filter-subindustry-${key}`)}
              hasSelectedSubindustry={hasSelectedSubindustry}
            >
              <PresetFilterName>
                {friendlyValue}
                {hasSelectedSubindustry && (
                  <RemoveOptionBox>
                    <FontAwesomeIcon icon={faClose} />
                  </RemoveOptionBox>
                )}
              </PresetFilterName>
            </PresetFilterItem>
          ))}
      </PresetFilterItems>
    </PresetFilter>
  ) : null;
};

export const FullDreamSearchRedirectButton = ({
  industry,
  zip,
}: {
  industry: GQL.VocationIndustry;
  zip: string;
}) => (
  <RedirectButton data-testid="full-search-redirect-button">
    <Link href={getUrls().DreamSearch({ industry, zip })}>
      <PresetFilter className="full_dream_search_redirect_button">
        <PresetFilterTitle>Browse more options</PresetFilterTitle>
        <RedirectButtonContent>
          See other {VocationIndustryToName[industry]} classes
        </RedirectButtonContent>
      </PresetFilter>
    </Link>
  </RedirectButton>
);

const RedirectButton = styled(Box)`
  a {
    text-decoration: none;
  }
`;

const PresetFilter = styled(Box)`
  width: 100%;
  background: ${({ theme }) => theme.palette.primary.main};
  border-radius: 1rem;
  padding: 1rem;
  margin-bottom: 2rem;
`;
const PresetFilterTitle = styled(Typography)`
  font-weight: bold;
  color: white;
  text-align: center;
  margin-bottom: 1rem;
`;
const PresetFilterItems = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;
const PresetFilterItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== "hasSelectedSubindustry",
})<{
  hasSelectedSubindustry: boolean;
}>`
  position: relative;
  height: 2.5rem;
  padding: ${({ hasSelectedSubindustry }) => (hasSelectedSubindustry ? "0.75rem 2rem" : "0.75rem")};
  background: white;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 2.5rem;
  cursor: pointer;
  user-select: none;
`;
const PresetFilterName = styled(Typography)`
  font-size: 14px;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`;
const RemoveOptionBox = styled(Box)`
  display: flex;
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
`;
const RedirectButtonContent = styled(Typography)`
  color: white;
  font-size: 14px;
  text-align: center;
  margin-top: -0.5rem;
`;
